'use client';

import { useTranslations } from 'next-intl';
import { MatchRow } from './MatchRow';
import { MatchCard } from './MatchCard';
import type { MatchWithRating } from './types';
import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Table, TableHeader } from '@/components/ui';
import { MatchCardsSkeleton, TableSkeleton } from '@/components/ui/skeletons';

interface MatchListGridProps {
  matches: MatchWithRating[];
  loading?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
  title?: string;
  locale: string;
  showBothPlayers?: boolean;
  showDate?: boolean;
  showRatings?: boolean;
  player1Data?: Record<
    string,
    {
      id: string;
      username: string;
      first_name: string;
      last_name: string;
      profile_photo: string | null;
      current_rating: number | null;
    }
  >;
  player2Data?: Record<
    string,
    {
      id: string;
      username: string;
      first_name: string;
      last_name: string;
      profile_photo: string | null;
      current_rating: number | null;
    }
  >;
}

export function MatchListGrid({
  matches,
  loading = false,
  emptyMessage,
  loadingMessage: _loadingMessage,
  title,
  showBothPlayers = false,
  showDate = true,
  showRatings = true,
  player1Data = {},
  player2Data = {},
}: MatchListGridProps) {
  const tMatches = useTranslations('Matches');
  const [isMobile, setIsMobile] = useState(false);

  const defaultEmptyMessage = tMatches('noMatches');
  const defaultTitle = tMatches('title');

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Mobile view: No outer card wrapper
  if (isMobile) {
    return (
      <div>
        {/* Title for mobile */}
        <h2 className="mb-3 text-lg font-semibold">{title || defaultTitle}</h2>

        {loading ? (
          <MatchCardsSkeleton count={3} />
        ) : matches.length === 0 ? (
          <div className="text-muted py-3 text-sm">{emptyMessage || defaultEmptyMessage}</div>
        ) : (
          // Mobile: Direct card layout without outer wrapper
          <div className="space-y-1.5">
            {matches.map((match) => (
              <MatchCard
                key={match.id}
                match={match}
                showBothPlayers={showBothPlayers}
                showRatings={showRatings}
                player1={player1Data[match.id]}
                player2={player2Data[match.id]}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop view: Keep card wrapper
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title || defaultTitle}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <TableSkeleton cols={5} rows={4} />
        ) : matches.length === 0 ? (
          <div className="text-muted px-4 py-3 text-sm">{emptyMessage || defaultEmptyMessage}</div>
        ) : (
          // Desktop: Table layout
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  {showDate && (
                    <TableHeader className="w-36">{tMatches('columns.date')}</TableHeader>
                  )}
                  {showBothPlayers ? (
                    <>
                      <TableHeader className="w-32">{tMatches('columns.time')}</TableHeader>
                      <TableHeader className="w-46">{tMatches('columns.player1')}</TableHeader>
                      <TableHeader className="w-46">{tMatches('columns.player2')}</TableHeader>
                    </>
                  ) : (
                    <TableHeader className="w-56">{tMatches('columns.opponent')}</TableHeader>
                  )}
                  <TableHeader className="hidden w-56 lg:table-cell">
                    {tMatches('columns.competition')}
                  </TableHeader>
                  <TableHeader className="w-36">{tMatches('columns.result')}</TableHeader>
                  <TableHeader className="hidden w-20 md:table-cell">
                    {tMatches('columns.venue')}
                  </TableHeader>
                  {showRatings &&
                    (showBothPlayers ? (
                      <>
                        <TableHeader className="w-24 text-right">
                          {tMatches('columns.player1Rating')}
                        </TableHeader>
                        <TableHeader className="hidden w-28 text-right sm:table-cell">
                          {tMatches('columns.player2Rating')}
                        </TableHeader>
                      </>
                    ) : (
                      <>
                        <TableHeader className="w-24 text-right">
                          {tMatches('columns.myRating')}
                        </TableHeader>
                        <TableHeader className="hidden w-28 text-right sm:table-cell">
                          {tMatches('columns.oppRating')}
                        </TableHeader>
                      </>
                    ))}
                  <TableHeader className="hidden w-24 text-center sm:table-cell">
                    {tMatches('columns.scorecard')}
                  </TableHeader>
                </tr>
              </thead>
              <tbody>
                {matches.map((match) => (
                  <MatchRow
                    key={match.id}
                    match={match}
                    showBothPlayers={showBothPlayers}
                    showDate={showDate}
                    showRatings={showRatings}
                    player1={player1Data[match.id]}
                    player2={player2Data[match.id]}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
